/* Wishes Made Easy — parent variant resolver (P0-1)
   Maps WME S.crew + S.tier → a variant id that exists on the Shopify
   product Essential Cart Drawer is bound to (the "parent" product on
   the Africa PDP). Never returns a WME sibling checkout id.

   Pure functions. Loaded by assets/wme-pdp.js in the theme, and required
   directly by tests/parent-variant.test.js. */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  root.WmeParentVariant = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var PACKAGE_TITLES = ['Basic', 'Premium', 'VIP'];

  function lookupMap(map, crewIndex, tierIndex) {
    if (!map || typeof map !== 'object') return null;
    var crewKey = String(crewIndex);
    var row = map[crewKey];
    if (row == null) row = map[crewIndex];
    if (row == null) return null;
    if (typeof row !== 'object') {
      return coerceId(row);
    }
    var id = row[String(tierIndex)];
    if (id == null) id = row[tierIndex];
    return coerceId(id);
  }

  function coerceId(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && isFinite(value) && value > 0) return value;
    var n = parseInt(String(value), 10);
    if (isNaN(n) || n <= 0) return null;
    return n;
  }

  function normalizeTitle(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\bthe\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function stemWords(s) {
    return normalizeTitle(s)
      .split(' ')
      .filter(Boolean)
      .map(function (w) {
        if (w.length > 3 && w.charAt(w.length - 1) === 's') return w.slice(0, -1);
        return w;
      })
      .join(' ');
  }

  function packageNameForTier(tierIndex) {
    return PACKAGE_TITLES[tierIndex] || null;
  }

  function isPackageTitle(s) {
    var n = normalizeTitle(s);
    return n === 'basic' || n === 'premium' || n === 'vip';
  }

  function scoreCrewAgainstOption(crew, optionValue) {
    if (!crew || !optionValue) return 0;
    var opt = stemWords(optionValue);
    if (!opt) return 0;
    var candidates = [];
    if (crew.name) candidates.push(stemWords(crew.name));
    if (crew.short) candidates.push(stemWords(crew.short));
    var best = 0;
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c) continue;
      if (c === opt) {
        best = 1;
        break;
      }
      var shorter = c.length <= opt.length ? c : opt;
      var longer = c.length <= opt.length ? opt : c;
      if (shorter.length >= 4 && longer.indexOf(shorter) !== -1) {
        best = Math.max(best, 0.85);
        continue;
      }
      var cTokens = c.split(' ');
      var oTokens = opt.split(' ');
      if (cTokens.length && cTokens.every(function (t) { return oTokens.indexOf(t) !== -1; })) {
        best = Math.max(best, 0.8);
      } else if (oTokens.length && oTokens.every(function (t) { return cTokens.indexOf(t) !== -1; })) {
        best = Math.max(best, 0.8);
      }
    }
    return best;
  }

  function variantOptions(variant) {
    var opts = [];
    if (!variant) return opts;
    if (variant.option1) opts.push(variant.option1);
    if (variant.option2) opts.push(variant.option2);
    if (variant.option3) opts.push(variant.option3);
    if (!opts.length && variant.title) {
      String(variant.title).split('/').forEach(function (part) {
        var t = part.trim();
        if (t) opts.push(t);
      });
    }
    return opts;
  }

  function parentProductVariants(cfg) {
    if (!cfg) return [];
    if (cfg.parentProduct && Array.isArray(cfg.parentProduct.variants)) {
      return cfg.parentProduct.variants;
    }
    if (Array.isArray(cfg.parentProductVariants)) return cfg.parentProductVariants;
    return [];
  }

  function parentProductForCrew(cfg, crew, crewIndex) {
    if (!cfg || !cfg.parentProduct || !crew) return false;
    var pp = cfg.parentProduct;
    if (Array.isArray(pp.forCrewIndexes)) {
      for (var i = 0; i < pp.forCrewIndexes.length; i++) {
        if (parseInt(pp.forCrewIndexes[i], 10) === crewIndex) return true;
      }
      return false;
    }
    if (pp.crewIndex != null && parseInt(pp.crewIndex, 10) === crewIndex) return true;
    var handle = String(pp.handle || '');
    var url = String(crew.url || '');
    if (handle && url) {
      var needle = '/products/' + handle;
      if (url.indexOf(needle) !== -1) return true;
      if (url.replace(/^\//, '') === ('products/' + handle)) return true;
    }
    return false;
  }

  function matchByOptionTitles(cfg, crewIndex, tierIndex) {
    var crews = (cfg && cfg.crews) || [];
    var crew = crews[crewIndex];
    var pack = packageNameForTier(tierIndex);
    if (!crew || !pack) return null;
    var variants = parentProductVariants(cfg);
    if (!variants.length) return null;

    var packNorm = normalizeTitle(pack);
    var hits = [];
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var opts = variantOptions(v);
      var packOpt = null;
      var teamOpts = [];
      for (var o = 0; o < opts.length; o++) {
        if (isPackageTitle(opts[o]) || normalizeTitle(opts[o]) === packNorm) {
          if (normalizeTitle(opts[o]) === packNorm) packOpt = opts[o];
        } else {
          teamOpts.push(opts[o]);
        }
      }
      if (!packOpt) continue;

      if (teamOpts.length) {
        var best = 0;
        for (var t = 0; t < teamOpts.length; t++) {
          best = Math.max(best, scoreCrewAgainstOption(crew, teamOpts[t]));
        }
        if (best >= 0.8) hits.push({ id: coerceId(v.id), score: best });
      } else if (parentProductForCrew(cfg, crew, crewIndex)) {
        /* Single-option parent (Package only) — accept only when Liquid
           marked this product as belonging to this crew, or the crew URL
           matches the parent handle. Never guess from Package alone. */
        hits.push({ id: coerceId(v.id), score: 0.9 });
      }
    }

    var exact = hits.filter(function (h) { return h.id && h.score === 1; });
    var pool = exact.length ? exact : hits.filter(function (h) { return h.id; });
    if (pool.length === 1) return pool[0].id;
    if (pool.length > 1) {
      var first = pool[0].id;
      for (var p = 1; p < pool.length; p++) {
        if (pool[p].id !== first) return null;
      }
      return first;
    }
    return null;
  }

  /**
   * Resolve the parent-product variant id for a WME crew + package tier.
   * Priority: explicit CFG.parentVariants[crew][tier], then option-title
   * match against CFG.parentProduct.variants. Returns null when unmapped —
   * callers must no-op the Essential bridge rather than write a wrong id.
   */
  function parentVariantIdFor(crewIndex, tierIndex, cfg) {
    var crew = parseInt(crewIndex, 10);
    var tier = parseInt(tierIndex, 10);
    if (isNaN(crew) || crew < 0) return null;
    if (isNaN(tier) || tier < 0 || tier > 2) return null;
    if (!cfg) return null;

    var mapped = lookupMap(cfg.parentVariants, crew, tier);
    if (mapped) return mapped;

    return matchByOptionTitles(cfg, crew, tier);
  }

  function siblingVariantIdFor(crewIndex, tierIndex, cfg) {
    var crews = (cfg && cfg.crews) || [];
    var c = crews[crewIndex];
    var t = c && c.tiers && c.tiers[tierIndex];
    return t && t.variantId ? coerceId(t.variantId) : null;
  }

  return {
    PACKAGE_TITLES: PACKAGE_TITLES,
    parentVariantIdFor: parentVariantIdFor,
    siblingVariantIdFor: siblingVariantIdFor,
    normalizeTitle: normalizeTitle,
    stemWords: stemWords,
    scoreCrewAgainstOption: scoreCrewAgainstOption,
    lookupMap: lookupMap
  };
});
